import { api } from '@/services/api';

export interface CostBreakdown {
  usd: number;
  brl: number;
}

export interface UserMetrics {
  totalRetention: number;
  totalPlays: number;
  totalViews: number;
  totalUniqueViews: number;
  totalClicks: number;
  videoCount: number;
}

export interface UserCostEntry {
  id_user: string;
  nome?: string;
  sobrenome?: string;
  email?: string;
  purchased_plan?: string;
  metrics: UserMetrics;
  costBreakdown: {
    networking: CostBreakdown;
    compute: CostBreakdown;
    storage: CostBreakdown;
    other: CostBreakdown;
  };
  totalCostUSD: number;
  totalCostBRL: number;
  estimatedTaxBRL: number;
  totalWithTaxBRL: number;
}

export interface CostByCategory {
  category: string;
  totalAmountUSD: number;
  totalAmountBRL: number;
  count: number;
}

export interface CostRates {
  networkingCostPerSecondUSD: number;
  computeCostPerPlayUSD: number;
  storageCostPerVideoUSD: number;
}

export interface UserCostReport {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalPlatformCostUSD: number;
    totalPlatformCostBRL: number;
    totalUserCostsUSD: number;
    totalUserCostsBRL: number;
    averageCostPerUserUSD: number;
    averageCostPerUserBRL: number;
    totalUsers: number;
    totalVideos: number;
    totalPlays: number;
    totalRetentionSeconds: number;
  };
  costRates: CostRates;
  costsByCategory: CostByCategory[];
  users: UserCostEntry[];
  chartData: {
    costDistribution: { userId: string; cost: number }[];
    categoryBreakdown: { name: string; value: number }[];
  };
}

export interface UserCostDetail {
  period: {
    startDate: string;
    endDate: string;
  };
  user: UserCostEntry;
  globalContext: {
    totalPlatformCostUSD: number;
    averageCostPerUserUSD: number;
    costRates: CostRates;
  };
}

export interface SyncSummaryResult {
  daysProcessed: number;
  rowsUpserted: number;
}

export const userCostAnalysisService = {
  getReport: async (startDate: string, endDate: string): Promise<UserCostReport> => {
    const response = await api.get(`/user-cost-analysis/report?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  getUserDetail: async (userId: string, startDate: string, endDate: string): Promise<UserCostDetail> => {
    const response = await api.get(`/user-cost-analysis/user/${userId}?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  syncSummary: async (startDate: string, endDate: string): Promise<SyncSummaryResult> => {
    const response = await api.post<{ success: boolean; message: string; data: SyncSummaryResult }>(
      `/user-cost-analysis/sync?startDate=${startDate}&endDate=${endDate}`,
    );
    return response.data;
  },
};

export default userCostAnalysisService;
